/*
 * Active chat transcript and compose area.
 *
 * This component handles optimistic message rendering, session-id
 * reconciliation after the first saved prompt, and the accessibility live
 * regions that announce loading state to assistive technology.
 */
import React from 'react';

import AcademicIntegrityDisclaimer from './AcademicIntegrityDisclaimer';
import ChatMessage from './ChatMessage';
import APIService from '../APIService'
import LoginForm from './LoginForm';
import { useSessionContext } from '../context/SessionContext';

function AssistantGrounding({ metadata }) {
  // Grounding metadata is optional because some replies may have no matched context.
  const grounding = metadata?.grounding;
  if (!grounding) {
    return null;
  }

  const sourceLabels = Array.isArray(grounding.labels) ? grounding.labels : [];

  return (
    <div className="assistant-grounding" aria-label="Sources used for this answer">
      <div className="assistant-grounding-summary">
        <span className="assistant-grounding-title">Based on</span>
        <span>{grounding.summary}</span>
      </div>
      <div className="assistant-grounding-footnote">
        <span>{grounding.groundingLabel}</span>
      </div>
      {sourceLabels.length > 0 && (
        <div className="assistant-source-chip-row">
          {sourceLabels.map((label) => (
            <span key={label} className="assistant-source-chip">
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ChatContainer({
  inputText,
  setInputText,
  activeChat,
  setActiveChat,
  questionLog,
  setQuestionLog,
  selectedCourseId,
  selectedAssignmentId,
  selectedAssignmentName,
}) {
  const { messagesBySession, setMessages, reconcileSessionId } = useSessionContext();

  let currentChat = null;
  for (const date in questionLog) {
    const chat = questionLog[date].find(c => c.sessionId === Number(activeChat));
    if (chat) {
      currentChat = chat;
      break;
    }
  }

  const chatMessages = React.useMemo(() => {
    if (activeChat === null || activeChat === undefined) {
      return [];
    }

    return messagesBySession[String(activeChat)] || [];
  }, [activeChat, messagesBySession]);
  const chatSessionId =
    currentChat?.chatSessionId ||
    currentChat?.sessionId ||
    (Number(activeChat) > 0 ? Number(activeChat) : null);
  const contextLabel = selectedCourseId
    ? `Course: ${selectedCourseId} | Assignment: ${selectedAssignmentName || "General"}`
    : "Course: None | Assignment: General";

  const [loading, setLoading] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState("");
  const textareaRef = React.useRef(null);

  const messagesContainerRef = React.useRef(null);
  React.useEffect(() => {
    if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  React.useEffect(() => {
    if (!textareaRef.current) {
      return;
    }

    textareaRef.current.style.height = "0px";
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`;
  }, [inputText]);

  const handlePromptSubmit = async (event) => {
    if (event) event.preventDefault();

      if (!inputText.trim() || !selectedCourseId) {
        console.warn("Cannot send message: No input or no selected course");
        return;
      }

      setLoading(true);
      setStatusMessage("Generating response.");

        const promptText = inputText;
        const newUserMessage = { type: "user", text: promptText };
        let requestSucceeded = false;
        const previousActiveChat = activeChat;
        const optimisticSessionId = chatSessionId || activeChat || (Date.now() * -1);
        const previousMessages = messagesBySession[String(optimisticSessionId)] || [];

          if (!chatSessionId && !activeChat) {
            setActiveChat(optimisticSessionId);
          }
          setMessages(optimisticSessionId, [...previousMessages, newUserMessage]);

          try {
          const response = await APIService.InsertPrompt(
            promptText,
            chatSessionId || null,
            selectedCourseId,
            selectedAssignmentId || null,
            selectedAssignmentName || null
          );
          const botMessage = response?.message || "Sorry, I didn't get that.";
          const botMetadata = response?.metadata || null;
          const resolvedSessionId = Number(response?.sessionId || response?.chat_session_id || chatSessionId);

          const newBotMessage = { type: "bot", text: botMessage, metadata: botMetadata };
          if (!Number.isInteger(resolvedSessionId) || resolvedSessionId <= 0) {
            throw new Error("Backend did not return a valid chat session id.");
          }

          if (String(optimisticSessionId) !== String(resolvedSessionId)) {
            // The backend creates the durable session id on first successful save.
            reconcileSessionId({
              temporarySessionId: optimisticSessionId,
              realSessionId: resolvedSessionId,
              sessionData: {
                chat_session_id: resolvedSessionId,
                courseCovered: selectedCourseId,
                assignmentId: selectedAssignmentId ? Number(selectedAssignmentId) : null,
                assignmentName: selectedAssignmentName || "",
                startTime: new Date().toISOString(),
              },
            });
          }

          setMessages(resolvedSessionId, (currentMessages) => [...currentMessages, newBotMessage]);

          setQuestionLog((prevLog) => {
            const updatedLog = { ...prevLog };
            const date = new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });

            if (!updatedLog[date]) {
              updatedLog[date] = [];
            }

            let targetDate = null;
            let chatIndex = -1;
            for (const existingDate in updatedLog) {
              const index = updatedLog[existingDate].findIndex(
                (chat) => chat.sessionId === resolvedSessionId
              );
              if (index !== -1) {
                targetDate = existingDate;
                chatIndex = index;
                break;
              }
            }

            if (targetDate === null) {
              updatedLog[date] = [...updatedLog[date], {
                sessionId: resolvedSessionId,
                chatSessionId: resolvedSessionId,
                prompt: new Date().toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }),
                courseId: selectedCourseId,
                courseCovered: selectedCourseId,
                assignmentId: selectedAssignmentId ? Number(selectedAssignmentId) : null,
                assignmentName: selectedAssignmentName || "",
              }];
            } else {
              const updatedChat = { ...updatedLog[targetDate][chatIndex] };
              updatedChat.chatSessionId = resolvedSessionId;
              updatedChat.courseId = selectedCourseId;
              updatedChat.courseCovered = selectedCourseId;
              updatedChat.assignmentId = selectedAssignmentId ? Number(selectedAssignmentId) : null;
              updatedChat.assignmentName = selectedAssignmentName || "";
              updatedLog[targetDate] = [...updatedLog[targetDate]];
              updatedLog[targetDate][chatIndex] = updatedChat;
            }

            return updatedLog;
          });
          setActiveChat(resolvedSessionId);
          requestSucceeded = true;
          setInputText('');
          } catch (error) {
          console.error("Error fetching response:", error);
          setMessages(optimisticSessionId, previousMessages);
          if (previousActiveChat !== optimisticSessionId) {
            setActiveChat(previousActiveChat ?? null);
          }
          alert(error.message || "Unable to generate a response right now.");
          setStatusMessage("Unable to generate a response right now.");
          }

          if (requestSucceeded) {
            setStatusMessage("AI Tutor response ready.");
          }
          setLoading(false);
          };

  React.useEffect(() => {
    setInputText('');
    setStatusMessage("");
  }, [activeChat, setInputText]);

  return (
    <main id="main-content" className="chat-container" aria-label="Student tutoring chat">
      <LoginForm forceOpen={false} onClose={() => {}}/>
      <AcademicIntegrityDisclaimer />

      <div className="chat-shell">
        <div
          ref={messagesContainerRef}
          className="flex-grow-1 overflow-auto chat-messages"
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
          aria-busy={loading}
          aria-label="Chat messages"
        >
        <div className="chat-inline-context" aria-label="Current tutoring context">{contextLabel}</div>
        {chatMessages.length === 0 ? (
            <div className="chat-empty-state">
              <div className="chat-empty-card">No messages yet</div>
            </div>
             ) : (
            chatMessages.map((msg, index) => (
              <ChatMessage
                key={index}
                role={msg.type === "user" ? "user" : "assistant"}
                content={msg.text}
                label={msg.type === "user" ? "You" : "AI Tutor"}
                footer={msg.type === "bot" ? (
                  <>
                    <AssistantGrounding metadata={msg.metadata} />
                    {msg.metadata?.question_category && (
                      <div className="message-meta">
                        {msg.metadata.question_category.replace(/_/g, " ")}
                        {msg.metadata.used_assignment_context ? " · assignment context" : ""}
                      </div>
                    )}
                  </>
                ) : null}
              />
            ))
          )}
        </div>

        <div className="chat-input-container">
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {statusMessage}
          </div>

          {loading && (
            <div className="loading-indicator" role="status" aria-live="polite">
              <div className="typing-dots" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span>AI Tutor is thinking...</span>
            </div>
          )}

          <div className="chat-compose-shell">
            <p id="chat-compose-help" className="sr-only">
              Type your question for the AI Tutor. Press Enter to send. Press Shift plus Enter to add a new line.
            </p>
            <div className="chat-compose-row">
              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={selectedCourseId ? "Ask for guidance" : "Select a course to start"}
                className="form-control"
                disabled={!selectedCourseId}
                rows={1}
                aria-label="Message the AI Tutor"
                aria-describedby="chat-compose-help"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handlePromptSubmit();
                  }
                }}
              />
              <button 
                onClick={(e) => handlePromptSubmit(e)} 
                className="btn send-btn-custom"
                disabled={!selectedCourseId || loading}
                aria-label="Send message"
              >{loading ? "Sending..." : "Send"}</button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ChatContainer;
